import React, { useEffect, useState } from 'react';
import { func, string, bool } from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Translate } from 'react-localize-redux';
import {
  hasSomePermission, manageSurveyPermission, participantsWritePermission
} from 'unihow-common';
import PopUpMenu from '../../PopUpMenu';
import sharedStyles from '../../../styles/shared';
import { withLang, langType } from '../../../i18n';
import {
  getSurveyParticipants,
  getSurveySchedule,
  patchSurveyCourseParticipantsUpdate
} from '../../../api';
import {
  addErrorMessageAction,
  setTaskPending,
  setTaskFulfilled
} from '../../../actions';

import {
  DISPLAY_DATE_FORMAT,
  LONG_DISPLAY_DATE_FORMAT
} from '../../../constants';
import {
  getCompiledPath, getSubRouteForSurveyType
} from '../../../util';
import useCurrentUser from '../../../hooks/useCurrentUser';

import styles from './participantList.module.css';

const { right, center } = sharedStyles;

const PAGE_SIZE = 100;

const getMaxPageNumber = (numEntries) => Math.floor(numEntries / PAGE_SIZE);

const ParticipantRow = ({
  lastName, firstName, studentNumber, degreeProgram,
  responseDate, displayActions, translate
}) => {
  const t = translate;
  const menuItems = [
    {
      action: (e) => e.preventDefault(),
      text: t('manageSurveys.removeAnswers'),
      confirmAction: true
    }
  ];

  return (
    <tr key={studentNumber}>
      <td>{lastName}</td>
      <td>{firstName}</td>
      <td>{studentNumber}</td>
      <td>{degreeProgram}</td>
      {(responseDate
        && (
        <td className={right} title={moment(responseDate).format(LONG_DISPLAY_DATE_FORMAT)}>
          {moment(responseDate).format(DISPLAY_DATE_FORMAT)}{ displayActions }
        </td>
        )) || <td />}
      {displayActions === true && (
      <td className={center}>
        <PopUpMenu hostId={studentNumber} title={t('manageSurveys.quickSteps')} menuItems={menuItems} />
      </td>
      )}
    </tr>
  );
};

ParticipantRow.propTypes = {
  firstName: string.isRequired,
  lastName: string.isRequired,
  studentNumber: string.isRequired,
  degreeProgram: string,
  responseDate: string,
  displayActions: bool.isRequired,
  translate: func.isRequired
};

ParticipantRow.defaultProps = {
  degreeProgram: '',
  responseDate: null
};

const ParticipantList = ({ surveyId, translate: t }) => {
  const [surveyParticipants, setSurveyParticipants] = useState([]);
  const [page, setPage] = useState(0);
  const [displayActions, setDisplayActions] = useState(false);
  const dispatch = useDispatch();
  const surveyType = useSelector((state) => state.survey.surveyOptions.surveyType);
  const { permissions: userPermissions } = useCurrentUser();

  const getParticipants = () => {
    dispatch(setTaskPending());
    patchSurveyCourseParticipantsUpdate(surveyId)
      .then(() => getSurveyParticipants(surveyId))
      .then((surveyParticipantsResponse) => {
        const maxPage = getMaxPageNumber(surveyParticipantsResponse.length);
        const pageNumberTooBig = page > maxPage;

        setSurveyParticipants(surveyParticipantsResponse);

        if (pageNumberTooBig) {
          setPage(maxPage);
        }
      })
      .catch((error) => dispatch(addErrorMessageAction(error)))
      .finally(() => dispatch(setTaskFulfilled()));
  };

  const displayActionsForEndedSurvey = () => {
    dispatch(setTaskPending());
    getSurveySchedule(surveyId)
      .then((schedule) => {
        if (!schedule.ended) {
          setDisplayActions(true);
        }
      })
      .catch((error) => dispatch(addErrorMessageAction(error)))
      .finally(() => dispatch(setTaskFulfilled()));
  };

  useEffect(() => {
    getParticipants();
    displayActionsForEndedSurvey();
  }, []);

  const changePage = (byAmount) => {
    setPage((prevPage) => prevPage + byAmount);
    window.scrollTo(0, 0);
  };

  const prevPage = () => changePage(-1);
  const nextPage = () => changePage(1);

  const buildRows = () => {
    const start = page * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return surveyParticipants && surveyParticipants.slice(start, end)
      .map((participant) => (
        <ParticipantRow
          key={participant.studentNumber}
          firstName={participant.firstName}
          lastName={participant.lastName}
          studentNumber={participant.studentNumber}
          degreeProgram={participant.degreeProgram}
          responseDate={participant.responseDate}
          displayActions={displayActions}
          translate={t}
        />
      ));
  };

  const renderPageControls = () => {
    const maxPage = getMaxPageNumber(surveyParticipants.length);

    if (surveyParticipants.length > PAGE_SIZE) {
      return (
        <div className={styles.pageControls}>
          <button
            type="button"
            className={`button ${page <= 0 ? 'disabled' : ''}`}
            disabled={page <= 0}
            onClick={prevPage}
          >
            <Translate id="common.previous" />
          </button>
          <span>
            {
              page * PAGE_SIZE + 1
            } - {
              Math.min(page * PAGE_SIZE + PAGE_SIZE, surveyParticipants.length)
            } / {
               surveyParticipants.length
             }
          </span>
          <button
            type="button"
            className={`button ${page >= maxPage ? 'disabled' : ''}`}
            disabled={page >= maxPage}
            onClick={nextPage}
          >
            <Translate id="common.next" />
          </button>
        </div>
      );
    }
    return null;
  };

  const showEditParticipantsLink = displayActions && (hasSomePermission(userPermissions, [
    manageSurveyPermission(surveyType),
    participantsWritePermission(surveyType)
  ]));

  return (
    <div className="listing">
      <h2 className="panel__title"><Translate id="manageSurveys.participants" /></h2>
      <div className={styles.usersHeaderContainer}>
        <h3><Translate id="manageSurveys.nParticipants" data={{ count: surveyParticipants.length }} /></h3>
        {showEditParticipantsLink
           && (
             <Link
               to={getCompiledPath(getSubRouteForSurveyType(surveyType).survey.participants.edit, { surveyId })}
               className="button--action"
               data-cy="edit-participants-button"
             >
               <Translate id="addSurveyParticipants.editParticipants" />
             </Link>
           )}
      </div>
      <table className="table-simple">
        <thead>
          <tr>
            <th><Translate id="common.lastName" /></th>
            <th><Translate id="common.firstName" /></th>
            <th><Translate id="common.studentNumber" /></th>
            <th><Translate id="common.degreeProgram" /></th>
            <th className={right}><Translate id="surveyUserList.responseDate" /></th>
            {displayActions && <th />}
          </tr>
        </thead>
        <tbody>
          {buildRows()}
        </tbody>
      </table>
      {renderPageControls()}
    </div>
  );
};

ParticipantList.propTypes = {
  surveyId: string.isRequired,
  ...langType
};

export default withLang(ParticipantList);
